/**
 * FOUC / first-paint local cache only. Authoritative prefs: client theme API (Starter+).
 */

import type { ThemeMode, ColorTheme } from '@/shared/types/theme.types'
import { DEFAULT_COLOR_THEME, isPresetThemeId } from '@/shared/theme/catalog'

export const THEME_STORAGE_KEY = 'mechagora-theme-v3'

export interface LocalThemeCache {
  mode: ThemeMode
  /** Preset id for FOUC; custom systems resolve after hydrate */
  colorTheme: ColorTheme
  activeKey?: string
}

const VALID_MODES: ThemeMode[] = ['light', 'dark', 'auto']

export function readLocalThemeCache(): LocalThemeCache | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LocalThemeCache>
    const mode = VALID_MODES.includes(parsed.mode as ThemeMode)
      ? (parsed.mode as ThemeMode)
      : null
    if (!mode) return null
    const colorTheme = isPresetThemeId(parsed.colorTheme)
      ? parsed.colorTheme
      : DEFAULT_COLOR_THEME
    return {
      mode,
      colorTheme,
      activeKey: typeof parsed.activeKey === 'string' ? parsed.activeKey : undefined,
    }
  } catch {
    return null
  }
}

export function writeLocalThemeCache(cache: LocalThemeCache): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(cache))
  } catch {
    /* private mode */
  }
}

export function resolveThemeType(
  mode: ThemeMode,
  system: 'light' | 'dark'
): 'light' | 'dark' {
  return mode === 'auto' ? system : mode
}

/** Back-compat names used by ThemeProvider */
export const readStoredTheme = readLocalThemeCache
export const writeStoredTheme = (p: {
  mode: ThemeMode
  colorTheme: ColorTheme
}) => writeLocalThemeCache(p)
